import React, { useState, useEffect } from 'react';
import { Link, useHistory, useParams } from 'react-router-dom';
import { fetchData } from '../../../apiService';
import { useAuth } from '../../../AuthContext';
import { tokenIsExpired } from '../../../authUtils';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  accent: '#2dd4bf', accentDark: '#14b8a6',
  accentDim: 'rgba(45,212,191,0.10)', accentBorder: 'rgba(45,212,191,0.22)',
  bg: '#f0f4f4', card: '#ffffff', cardBorder: 'rgba(0,0,0,0.06)',
  text: '#1a2e2e', muted: '#5a7a7a', shadow: '0 2px 10px rgba(0,0,0,0.07)',
  danger: '#ef4444', dangerDark: '#dc2626',
};

const inp  = { border: `1px solid ${C.cardBorder}`, borderRadius: '7px', fontSize: '12px', height: '28px', width: '100%', padding: '0 8px', outline: 'none', backgroundColor: '#f8fafa', boxSizing: 'border-box', fontFamily: 'inherit' };
const inpR = { ...inp, textAlign: 'right' };
const card = { backgroundColor: C.card, borderRadius: '10px', boxShadow: C.shadow, border: `1px solid ${C.cardBorder}`, marginBottom: '10px', overflow: 'hidden' };
const cardHead = (color = C.accent) => ({ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 12px', borderBottom: `1px solid ${C.cardBorder}`, backgroundColor: `${color}18`, fontSize: '10px', fontWeight: 700, color: C.text, textTransform: 'uppercase', letterSpacing: '0.5px' });
const cardBody = { padding: '10px 12px' };
const lbl = { fontSize: '9.5px', fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '3px' };

const JournalEntryDetails = () => {
  const { id } = useParams();
  const history = useHistory();
  const { isAuthenticated, companyId, branchId, userId, token, logout } = useAuth();

  const [selectedItems, setSelectedItems] = useState([]);
  const [formData, setFormData] = useState({
    CompanyId: companyId,
    IsType: true,
    BillDate: '',
    BillNo: '',
    FinYear: '',
    Remarks: '',
    Amount: '',
    MasterName: '',
    IsIGST: false,
    GSTRate: 0,
    CGSTAmount: '0.00',
    SGSTAmount: '0.00',
    IGSTAmount: '0.00',
    RoundOff: '0.00',
    TotalAmount: '0.00',
  });

  const formatDateForInput = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (isAuthenticated && tokenIsExpired(token)) { logout(); return; }
    fetchData('JournelEntryAPI', `/${id}`, token)
      .then(res => {
        if (res.responseCode === '1111') {
          setFormData(res.response_data.JournalEntry);
          setSelectedItems(res.response_data.JournalEntryDetails);
        }
      })
      .catch(err => console.error('Error fetching journal entry details:', err));
  }, [id]);

  return (
    <div style={{ fontFamily: 'Segoe UI, sans-serif', fontSize: '12px', width: '100%', padding: '6px', boxSizing: 'border-box', backgroundColor: C.bg }}>

      {/* ── Header ── */}
      <div style={{ ...card, overflow: 'visible' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 12px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <AccountBalanceWalletIcon style={{ fontSize: 16, color: C.accent }} />
            <span style={{ fontWeight: 800, fontSize: '13px', color: C.text, fontFamily: 'Inter, sans-serif' }}>Journal Entry Details</span>
          </div>

          {/* Debit / Credit indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#1d4ed8' }}>
              <input type="radio" name="IsType" value="1" checked={formData.IsType === true} readOnly />
              Debit
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: '5px', fontSize: '12px', fontWeight: 600, color: '#b45309' }}>
              <input type="radio" name="IsType" value="0" checked={formData.IsType === false} readOnly />
              Credit
            </label>
          </div>

          <div style={{ display: 'flex', gap: '8px' }}>
            <Link to={`/JournalEntryEdit/${id}`}
              style={{ height: '28px', padding: '0 16px', borderRadius: '7px', border: 'none', backgroundColor: C.accent, color: '#fff', fontWeight: 700, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '4px', textDecoration: 'none', fontFamily: 'inherit' }}>
              ✎ Edit
            </Link>
            <Link to={{ pathname: '/JournelEntryIndex', state: { from: '/JournalEntryDetails' } }}
              onClick={() => localStorage.setItem('shouldReload', 'true')}
              style={{ height: '28px', padding: '0 14px', borderRadius: '7px', border: `1px solid ${C.cardBorder}`, backgroundColor: C.card, color: C.muted, fontWeight: 600, fontSize: '11px', cursor: 'pointer', display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
              ← Back
            </Link>
          </div>
        </div>
      </div>

      {/* ── Entry Details ── */}
      <div style={card}>
        <div style={cardHead()}>📋 Entry Details</div>
        <div style={{ ...cardBody, display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '10px' }}>
          <div>
            <label style={lbl}>Master</label>
            <input type="text" value={formData.MasterName || ''} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Date</label>
            <input type="date" value={formatDateForInput(formData.BillDate)} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Number</label>
            <input type="text" value={`${formData.BillNo || ''}/${formData.FinYear || ''}`} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Remarks</label>
            <input type="text" value={formData.Remarks || ''} readOnly style={inp} />
          </div>
          <div>
            <label style={lbl}>Amount</label>
            <input type="number" value={formData.Amount || ''} readOnly style={{ ...inpR, fontWeight: 700, color: '#0D47A1' }} />
          </div>
          <div>
            <label style={{ ...lbl, display: 'flex', alignItems: 'center', gap: '6px' }}>
              GST %
              <input type="checkbox" checked={formData.IsIGST || false} readOnly />
              <span style={{ color: C.danger, fontSize: '9px' }}>IGST</span>
            </label>
            <select value={formData.GSTRate || 0} disabled style={{ ...inp, cursor: 'default', backgroundColor: '#f0f4f4' }}>
              {[0, 3, 5, 12, 18, 28].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
        </div>

        {/* GST summary bar */}
        <div style={{ display: 'flex', gap: '20px', padding: '6px 12px', borderTop: `1px solid ${C.cardBorder}`, backgroundColor: '#f8fafa', flexWrap: 'wrap' }}>
          {[['CGST', formData.CGSTAmount], ['SGST', formData.SGSTAmount], ['IGST', formData.IGSTAmount], ['Round Off', formData.RoundOff], ['Total', formData.TotalAmount]].map(([label, val]) => (
            <div key={label} style={{ fontSize: '11px', color: C.muted }}>
              {label}: <strong style={{ color: '#B71C1C' }}>{val}</strong>
            </div>
          ))}
        </div>
      </div>

      {/* ── Master List ── */}
      <div style={card}>
        <div style={cardHead('#1F2A38')}>📒 Selected Master List ({selectedItems.length})</div>
        <div style={{ maxHeight: '360px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ backgroundColor: '#1F2A38', color: '#fff', position: 'sticky', top: 0 }}>
              <tr>
                {['#', 'Party', 'Amount'].map((h, i) => (
                  <th key={i} style={{ padding: '6px 8px', textAlign: i === 2 ? 'right' : 'left', fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.4px', whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {selectedItems.length === 0 ? (
                <tr><td colSpan={3} style={{ textAlign: 'center', padding: '20px', color: C.muted, fontSize: '12px' }}>No items found</td></tr>
              ) : selectedItems.map((item, index) => (
                <tr key={index} style={{ backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: `1px solid ${C.cardBorder}` }}>
                  <td style={{ padding: '3px 8px', color: C.muted, fontSize: '11px' }}>{index + 1}</td>
                  <td style={{ padding: '3px 8px', fontWeight: 600 }}>{item.MasterName}</td>
                  <td style={{ padding: '3px 8px', textAlign: 'right' }}>
                    <input type="number" value={item.Amount} readOnly
                      style={{ ...inpR, height: '24px', borderRadius: '4px', backgroundColor: '#fffbeb', fontWeight: 700 }} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

export default JournalEntryDetails;
